import { useEffect, useRef, useState, FormEvent, ReactNode } from 'react'

// --- TYPESCRIPT INTERFACES ---
interface BlogPost {
    id: string
    title: string
    excerpt: string
    date: string
    category: string
    image: string
}

// --- MODERN SCROLL REVEAL WRAPPER ---
interface RevealProps {
    children: ReactNode
    delay?: number
}

function RevealOnScroll({ children, delay = 0 }: RevealProps) {
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const element = ref.current
        if (!element) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible')
                    observer.unobserve(entry.target)
                }
            },
            { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
        )

        observer.observe(element)
        return () => observer.disconnect()
    }, [])

    return (
        <div
            ref={ref}
            className="reveal-on-scroll"
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    )
}

// --- BLOG DATA ---
const blogPosts: BlogPost[] = [
    {
        id: 'tips-choosing-plan',
        title: '5 Tips for Choosing the Right Insurance Plan',
        excerpt: 'Learn how to evaluate insurance options and find the perfect coverage for your needs.',
        date: 'March 28, 2024',
        category: 'Insurance Tips',
        image: 'https://picsum.photos/seed/blog1/600/400'
    },
    {
        id: 'understanding-claims',
        title: 'Understanding Insurance Claims',
        excerpt: 'A comprehensive guide to the insurance claims process and how to get the most out of your policy.',
        date: 'March 25, 2024',
        category: 'Education',
        image: 'https://picsum.photos/seed/blog2/600/400'
    },
    {
        id: 'regular-reviews',
        title: 'The Importance of Regular Coverage Reviews',
        excerpt: "Why it's essential to review your insurance coverage annually to stay protected.",
        date: 'March 20, 2024',
        category: 'Best Practices',
        image: 'https://picsum.photos/seed/blog3/600/400'
    },
    {
        id: 'business-insurance',
        title: 'Business Insurance: What You Need to Know',
        excerpt: 'Essential information about business insurance coverage and requirements for entrepreneurs.',
        date: 'March 15, 2024',
        category: 'Business',
        image: 'https://picsum.photos/seed/blog4/600/400'
    },
    {
        id: 'lower-premiums',
        title: 'How to Lower Your Insurance Premiums',
        excerpt: 'Strategies and tips to reduce your insurance costs without sacrificing coverage.',
        date: 'March 10, 2024',
        category: 'Money Saving',
        image: 'https://picsum.photos/seed/blog5/600/400'
    },
    {
        id: 'protecting-family',
        title: "Protecting Your Family's Future",
        excerpt: "A guide to life insurance and how it helps secure your family's financial future.",
        date: 'March 5, 2024',
        category: 'Family Protection',
        image: 'https://picsum.photos/seed/blog6/600/400'
    }
]

// --- MAIN COMPONENT ---
export default function Blog() {
    const [email, setEmail] = useState('')

    const handleSubscribe = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        console.log('Subscribed with:', email)
        setEmail('')
    }

    return (
        /* 
          ADDED GRADIENT HERE: 
          bg-gradient-to-br goes from top-left to bottom-right.
          from-slate-50 via-white to-blue-50 gives a clean, modern, professional look.
          min-h-screen ensures it covers the full page height.
        */
        <main className="pt-32 pb-20 min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* Header */}
                <RevealOnScroll>
                    <div className="text-center mb-16">
                        <h1 className="text-5xl md:text-6xl font-display font-bold text-dark mb-4">
                            Our Blog
                        </h1>
                        <p className="text-xl text-gray-body max-w-3xl mx-auto">
                            Expert insights and tips on insurance, protection, and financial wellness.
                        </p>
                    </div>
                </RevealOnScroll>

                {/* Blog Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
                    {blogPosts.map((post, idx) => (
                        <RevealOnScroll key={post.id} delay={idx * 50}>
                            <article className="service-card group rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow h-full flex flex-col">
                                <div className="h-48 overflow-hidden">
                                    <img
                                        src={post.image}
                                        alt={post.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="p-6 flex flex-col flex-grow">
                                    <div className="flex justify-between items-start mb-3">
                                        <span className="text-xs font-body font-semibold text-brand bg-light px-3 py-1 rounded">
                                            {post.category}
                                        </span>
                                        <time className="text-xs text-gray-body" dateTime={post.date}>
                                            {post.date}
                                        </time>
                                    </div>
                                    <h3 className="text-lg font-display font-bold text-dark mb-2 line-clamp-2">
                                        {post.title}
                                    </h3>
                                    <p className="text-gray-body text-sm mb-4 line-clamp-2 flex-grow">
                                        {post.excerpt}
                                    </p>
                                    <button className="text-brand hover:text-brand-dark font-body font-medium transition inline-flex items-center gap-1 self-start">
                                        Read More
                                        <span className="group-hover:translate-x-1 transition-transform">→</span>
                                    </button>
                                </div>
                            </article>
                        </RevealOnScroll>
                    ))}
                </div>

                {/* Newsletter Section */}
                <RevealOnScroll>
                    <section className="bg-light/60 backdrop-blur-sm border border-white/50 py-12 px-8 rounded-lg text-center shadow-sm">
                        <h2 className="text-3xl font-display font-bold text-dark mb-4">
                            Get Insurance Tips in Your Inbox
                        </h2>
                        <p className="text-gray-body mb-6 max-w-2xl mx-auto">
                            Subscribe to our newsletter for the latest insurance insights and expert advice.
                        </p>

                        <form onSubmit={handleSubscribe} className="flex gap-2 max-w-md mx-auto">
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                className="flex-grow px-4 py-3 rounded border border-gray-300 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand bg-white"
                            />
                            <button type="submit" className="btn-primary px-6 py-3 rounded font-body font-medium whitespace-nowrap">
                                Subscribe
                            </button>
                        </form>
                    </section>
                </RevealOnScroll>

            </div>
        </main>
    )
}